import { useEffect } from 'react'

const DEFAULT_SITE_NAME = 'WINRATES.GG'
const DEFAULT_TITLE = 'WINRATES.GG™ | The unified League of Legends® stats companion app — For Winners'
const DEFAULT_DESCRIPTION = 'WINRATES.GG is the unified League of Legends® companion app, bringing player profiles, champion analytics, the best item builds and rune builds, ranked tier lists, match history, live games, mastery leaderboards, and detailed player and champion stats together in one place. Get advanced insights for free, including champion scaling profiles, Red Kayn vs. Blue Kayn stats and best Kayn form analysis, champion evolution stats and best evolution order, first item and rush item completion timing, jungle and support item completion times, quest stats, and True Lead—an objective measure of how your team is actually performing.'
const DEFAULT_SHARE_IMAGE = '/share-card.png'
const DEFAULT_SHARE_IMAGE_ALT = 'WINRATES.GG — The unified League of Legends stats companion app — For Winners.'
const DEFAULT_THEME_COLOR = '#16161b'

function getAbsoluteUrl(url) {
    if (typeof window === 'undefined') {
        return url
    }

    if (!url) {
        return window.location.href
    }

    try {
        return new URL(url, window.location.origin).toString()
    } catch {
        return window.location.href
    }
}

function upsertMeta(attribute, key, content) {
    if (typeof document === 'undefined') {
        return
    }

    let element = document.head.querySelector(`meta[${attribute}="${key}"]`)

    if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attribute, key)
        document.head.appendChild(element)
    }

    element.setAttribute('content', content)
}

function upsertLink(rel, href) {
    if (typeof document === 'undefined') {
        return
    }

    let element = document.head.querySelector(`link[rel="${rel}"]`)

    if (!element) {
        element = document.createElement('link')
        element.setAttribute('rel', rel)
        document.head.appendChild(element)
    }

    element.setAttribute('href', href)
}

function removeStructuredData() {
    if (typeof document === 'undefined') {
        return
    }

    const existing = document.getElementById('wrgg-structured-data')
    if (existing) {
        existing.remove()
    }
}

const Seo = ({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    canonical,
    robots = 'index, follow, max-image-preview:large',
    type = 'website',
    image = DEFAULT_SHARE_IMAGE,
    structuredData,
}) => {
    const serializedStructuredData = structuredData ? JSON.stringify(structuredData) : ''

    useEffect(() => {
        const resolvedUrl = getAbsoluteUrl(canonical)
        const resolvedImage = image ? getAbsoluteUrl(image) : ''

        document.title = title
        upsertMeta('name', 'description', description)
        upsertMeta('name', 'robots', robots)
        upsertMeta('name', 'theme-color', DEFAULT_THEME_COLOR)
        upsertMeta('property', 'og:site_name', DEFAULT_SITE_NAME)
        upsertMeta('property', 'og:type', type)
        upsertMeta('property', 'og:title', title)
        upsertMeta('property', 'og:description', description)
        upsertMeta('property', 'og:url', resolvedUrl)
        upsertMeta('property', 'og:image:alt', DEFAULT_SHARE_IMAGE_ALT)
        upsertMeta('name', 'twitter:card', resolvedImage ? 'summary_large_image' : 'summary')
        upsertMeta('name', 'twitter:title', title)
        upsertMeta('name', 'twitter:description', description)
        upsertMeta('name', 'twitter:image:alt', DEFAULT_SHARE_IMAGE_ALT)
        upsertLink('canonical', resolvedUrl)

        if (resolvedImage) {
            upsertMeta('property', 'og:image', resolvedImage)
            upsertMeta('name', 'twitter:image', resolvedImage)
        }

        removeStructuredData()

        if (serializedStructuredData) {
            const script = document.createElement('script')
            script.id = 'wrgg-structured-data'
            script.type = 'application/ld+json'
            script.textContent = serializedStructuredData
            document.head.appendChild(script)
        }

        return () => {
            removeStructuredData()
        }
    }, [canonical, description, image, robots, serializedStructuredData, title, type])

    return null
}

export default Seo
